import { motion } from "framer-motion";
import {
  CalendarCheck, MessageSquare, Phone, Sparkles, CheckCircle2, Bell,
} from "lucide-react";

/* Mobile-only phone-shaped mockup of the Clinic OS app.
   Used on /home in <MeetClinicOS> to give mobile visitors something to "see"
   right after the headline. Hidden on md+ where the bigger ProductMockup runs. */

export const MobileAppPreview = () => {
  return (
    <div
      data-testid="mobile-app-preview"
      className="relative mx-auto mt-10 w-full max-w-[280px] sm:hidden"
    >
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute -inset-8 -z-10 rounded-[60px] opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle at 50% 30%, rgba(124,58,237,0.45), transparent 60%)" }}
      />

      {/* Phone frame */}
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.96 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="relative rounded-[40px] border border-foreground/15 bg-foreground/85 p-2.5 shadow-2xl shadow-violet-950/40 dark:bg-black/80"
      >
        {/* Side speaker / notch */}
        <span aria-hidden className="absolute left-1/2 top-3 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-black/40" />

        {/* Screen */}
        <div className="relative overflow-hidden rounded-[32px] bg-background">
          {/* Status bar */}
          <div className="flex items-center justify-between px-4 pb-2 pt-5 text-[10px] font-bold text-foreground/90">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span className="h-2 w-3 rounded-sm border border-foreground/70 px-px">
                <span className="block h-full w-3/4 rounded-[1px] bg-emerald-500" />
              </span>
            </span>
          </div>

          {/* App header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-violet-600 font-display text-xs font-black text-white shadow-md">
                F
              </span>
              <div className="leading-tight">
                <p className="text-[11.5px] font-display font-black tracking-tight">Clinic OS</p>
                <p className="text-[9px] text-muted-foreground">Apex Physio · Mon</p>
              </div>
            </div>
            <Bell className="h-3.5 w-3.5 text-muted-foreground" />
          </div>

          {/* Body */}
          <div className="space-y-3 p-3.5">
            {/* KAI alert card */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/12 via-fuchsia-500/8 to-transparent p-3"
            >
              <div className="flex items-start gap-2">
                <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
                  <Sparkles className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-300">
                    KAI · just now
                  </p>
                  <p className="mt-1 text-[11.5px] font-bold leading-snug text-foreground">
                    Booked 3 patients while you were with a client.
                  </p>
                  <p className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">
                    All confirmed. Inbox is clean.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Stat strip */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="grid grid-cols-3 gap-1.5"
            >
              {[
                { k: "+24%", v: "Booked" },
                { k: "4.9★",  v: "Rating" },
                { k: "0",     v: "Missed" },
              ].map((s) => (
                <div key={s.v} className="rounded-xl border border-border bg-card p-2 text-center">
                  <p className="font-display text-[12px] font-black tracking-tight">{s.k}</p>
                  <p className="text-[8.5px] uppercase tracking-[0.14em] text-muted-foreground">{s.v}</p>
                </div>
              ))}
            </motion.div>

            {/* Schedule row */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.55, duration: 0.4 }}
              className="rounded-2xl border border-border bg-card p-3"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Up next · 2:30 PM</p>
              <p className="mt-1 text-[11.5px] font-bold leading-snug">Maria Lopez · follow-up</p>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-300">
                  <CheckCircle2 className="h-2.5 w-2.5" />
                  Confirmed
                </span>
                <span className="text-[9px] text-muted-foreground">Jane App</span>
              </div>
            </motion.div>

            {/* Bottom tab bar */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.65, duration: 0.4 }}
              className="mt-2 flex items-center justify-around rounded-2xl border border-border bg-card px-2 py-2"
            >
              {[
                { Icon: CalendarCheck, label: "Calendar", active: true },
                { Icon: MessageSquare, label: "Inbox", n: 3 },
                { Icon: Phone, label: "Calls" },
                { Icon: Sparkles, label: "KAI" },
              ].map((t) => (
                <div key={t.label} className="relative flex flex-col items-center">
                  <t.Icon className={`h-3.5 w-3.5 ${t.active ? "text-violet-500" : "text-muted-foreground"}`} />
                  <span className={`mt-0.5 text-[8.5px] font-semibold ${t.active ? "text-violet-500" : "text-muted-foreground"}`}>
                    {t.label}
                  </span>
                  {t.n && (
                    <span className="absolute -right-1.5 -top-1 inline-flex h-3 w-3 items-center justify-center rounded-full bg-rose-500 text-[7.5px] font-bold text-white">
                      {t.n}
                    </span>
                  )}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Home indicator */}
          <div className="flex justify-center pb-1.5 pt-1">
            <span className="h-1 w-20 rounded-full bg-foreground/30" />
          </div>
        </div>
      </motion.div>

      {/* Floating accent — "KAI" badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
        whileInView={{ opacity: 1, scale: 1, rotate: -6 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5, duration: 0.5, ease: "backOut" }}
        className="absolute -left-3 top-16 rounded-xl border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-[9.5px] font-bold uppercase tracking-[0.18em] text-violet-600 backdrop-blur-sm dark:text-violet-200"
      >
        AI · live
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: 8 }}
        whileInView={{ opacity: 1, scale: 1, rotate: 5 }}
        viewport={{ once: true }}
        transition={{ delay: 0.6, duration: 0.5, ease: "backOut" }}
        className="absolute -right-3 bottom-24 rounded-xl border border-emerald-500/40 bg-emerald-500/15 px-2 py-1 text-[9.5px] font-bold uppercase tracking-[0.18em] text-emerald-600 backdrop-blur-sm dark:text-emerald-200"
      >
        EMR-sync
      </motion.div>
    </div>
  );
};
